"use client";

import React, { useState, useMemo } from "react";
import { ArrowRight, ArrowLeft, CircleCheck, Send, User, Building2, Globe, Wallet, MessageSquare, Phone, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RangeInput } from "@/components/ui/range-input";
import { Toaster } from "@/components/ui/sonner";
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={`flex h-12 w-full rounded-none border-b border-input bg-transparent px-0 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary transition-all disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const BUDGET_OPTIONS = ["< €2.500", "€2.500 - €7.500", "€7.500 - €15.000", "> €15.000"];
const WEBSITE_TYPES = ["Maatwerk Website", "E-commerce Platform", "Digitaal Portfolio", "Corporate Site", "Web Applicatie"];
function ConfiguratorForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    formState: {
      errors
    }
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 2;
  const nextStep = async () => {
    setStep(2);
  };
  const prevStep = () => {
    setStep(1);
  };
  if (isSubmitted && !submitError) {
    return <div data-section-id="61137" className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-secondary/20 flex items-center justify-center border border-secondary/50 shadow-[0_0_30px_-5px_rgba(0,255,255,0.4)]">
          <CircleCheck className="size-10 text-secondary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Aanvraag Ontvangen!</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {successMessage || "Bedankt voor uw aanvraag. Onze strategen nemen binnen 24 uur contact met u op om uw digitale ambities te bespreken."}
          </p>
        </div>
        <Button variant="outline" onClick={() => window.location.reload()} className="mt-4 border-primary/20 hover:bg-primary/10 hover:text-primary transition-all duration-300">
          Nieuwe configuratie starten
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="relative mb-8">
        <div className="absolute top-1/2 left-0 w-full h-1 bg-muted/20 rounded-full -z-10"></div>
        <div className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-primary to-secondary rounded-full -z-10 transition-all duration-500 ease-out" style={{
        width: `${(step - 1) / (totalSteps - 1) * 100}%`
      }}></div>
        <div className="flex justify-between w-full">
          {[1, 2].map(s => <div key={s} className={`flex items-center justify-center size-10 rounded-full border-2 transition-all duration-500 ${step >= s ? "bg-primary border-primary text-primary-foreground shadow-[0_0_20px_rgba(168,85,247,0.4)]" : "bg-background border-muted text-muted-foreground"}`}>
              {step > s ? <CircleCheck className="size-6" /> : <span className="text-sm font-bold">{s}</span>}
            </div>)}
        </div>
        <div className="flex justify-between w-full mt-4 text-[10px] uppercase tracking-widest font-bold text-muted-foreground px-1">
          <span>Identiteit</span>
          <span>Specificaties</span>
        </div>
      </div>

      {}
      <div className={step === 1 ? "block animate-in slide-in-from-left-4 fade-in duration-300" : "hidden"}>
        <div className="grid gap-8 md:grid-cols-2">
          <FormField control={control} name="naam" rules={{
          required: "Naam is verplicht"
        }} render={({
          field
        }) => <FormItem className="col-span-2 md:col-span-1">
                <FormLabel className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Uw Naam <span className="text-primary">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <User className="absolute left-0 top-3 size-4 text-muted-foreground/40" />
                    <Input placeholder="Bijv. Jasper van den Berg" className="pl-8" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="bedrijfsnaam" render={({
          field
        }) => <FormItem className="col-span-2 md:col-span-1">
                <FormLabel className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Organisatie</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Building2 className="absolute left-0 top-3 size-4 text-muted-foreground/40" />
                    <Input placeholder="Uw bedrijfsnaam" className="pl-8" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "E-mailadres is verplicht",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Ongeldig e-mailadres"
          }
        }} render={({
          field
        }) => <FormItem className="col-span-2 md:col-span-1">
                <FormLabel className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Zakelijk E-mailadres <span className="text-primary">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-0 top-3 size-4 text-muted-foreground/40" />
                    <Input type="email" placeholder="jasper@bedrijf.nl" className="pl-8" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="telefoon" rules={{
          required: "Telefoonnummer is verplicht"
        }} render={({
          field
        }) => <FormItem className="col-span-2 md:col-span-1">
                <FormLabel className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Telefoon <span className="text-primary">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <Phone className="absolute left-0 top-3 size-4 text-muted-foreground/40" />
                    <Input type="tel" placeholder="+31 (0) 6 12 34 56 78" className="pl-8" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className={step === 2 ? "block animate-in slide-in-from-right-4 fade-in duration-300" : "hidden"}>
        <div className="space-y-8">
          <FormField control={control} name="website_type" rules={{
          required: "Selecteer een type website"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Digitale Oplossing <span className="text-primary">*</span></FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full pl-8 relative border-b border-t-0 border-l-0 border-r-0 rounded-none bg-transparent px-0 h-12 focus:ring-0 focus:border-primary">
                      <Globe className="absolute left-0 top-3 size-4 text-muted-foreground/40 z-10" />
                      <SelectValue placeholder="Selecteer een type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-background/95 backdrop-blur-xl border-white/10">
                    {WEBSITE_TYPES.map(type => <SelectItem key={type} value={type} className="focus:bg-primary/20">
                        {type}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="budget" render={({
          field
        }) => <FormItem className="space-y-6">
                <div className="flex justify-between items-center">
                  <FormLabel className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Investeringindicatie</FormLabel>
                  <span className="text-xs font-bold text-secondary bg-secondary/10 px-4 py-1.5 rounded-full border border-secondary/20 tracking-wide">
                    {field.value || "Kies een indicatie"}
                  </span>
                </div>
                <FormControl>
                  <div className="pt-4 px-1">
                    <RangeInput min={0} max={BUDGET_OPTIONS.length - 1} step={1} value={BUDGET_OPTIONS.indexOf(field.value) !== -1 ? BUDGET_OPTIONS.indexOf(field.value) : 0} onValueChange={val => field.onChange(BUDGET_OPTIONS[val])} className="accent-primary h-2" />
                    <div className="flex justify-between mt-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                      <span>&lt; €2.5k</span>
                      <span>€15k+</span>
                    </div>
                  </div>
                </FormControl>
                <FormDescription className="text-xs italic text-muted-foreground/70">
                  Verschuif de indicator om uw investeringsbereidheid aan te geven.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="extra_informatie" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Projectomschrijving & Doelen</FormLabel>
                <FormControl>
                  <div className="relative">
                    <MessageSquare className="absolute left-0 top-4 size-4 text-muted-foreground/40" />
                    <Textarea placeholder="Beschrijf uw doelen, gewenste interacties en specifieke functionaliteiten..." className="min-h-[140px] pl-8 rounded-none border-b border-t-0 border-l-0 border-r-0 bg-transparent resize-none focus-visible:ring-0 focus-visible:border-primary text-base" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="flex justify-between pt-8 border-t border-white/5">
        {step === 1 ? <div className="w-full flex justify-end">
            <Button type="button" onClick={nextStep} className="w-full md:w-auto group bg-primary hover:bg-primary/90 text-white rounded-lg px-8 py-6 text-base font-bold transition-all duration-300 shadow-lg shadow-primary/20">
              Volgende
              <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div> : <>
            <Button type="button" variant="outline" onClick={prevStep} className="group border-white/10 hover:bg-white/5 text-muted-foreground hover:text-foreground px-6 py-6 rounded-lg transition-all duration-300">
              <ArrowLeft className="mr-2 size-5 transition-transform group-hover:-translate-x-1" />
              Terug
            </Button>
            <Button type="submit" disabled={isSubmitting} className="min-w-[180px] bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold rounded-lg px-8 py-6 text-base transition-all duration-300 shadow-xl shadow-primary/20">
              {isSubmitting ? "Verwerken..." : <>
                  Aanvraag Versturen
                  <Send className="ml-2 size-5" />
                </>}
            </Button>
          </>}
      </div>

      {submitError && <div className="p-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2">
          <span className="font-extrabold uppercase tracking-tighter">Systeemfout:</span> {submitError}
        </div>}
    </Form>;
}
export default function OfferteConfigurator() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="laten-we-iets-bouwen" className="dark bg-background text-foreground py-32 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-15%] right-[-10%] w-[600px] h-[600px] bg-primary/20 rounded-full blur-[120px] opacity-40 animate-pulse"></div>
        <div className="absolute bottom-[-15%] left-[-10%] w-[600px] h-[600px] bg-secondary/20 rounded-full blur-[120px] opacity-40"></div>
      </div>

      <div className="container mx-auto max-w-4xl relative z-10">
        <div ref={ref} className={`transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}>
          <div className="text-center mb-16 space-y-6">
            <h2 className="text-5xl md:text-7xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-white via-primary/80 to-secondary leading-tight">
              Laten We Iets Bouwen
            </h2>
            <p className="text-muted-foreground text-xl max-w-2xl mx-auto leading-relaxed font-medium">
              Bij North Websites vertalen we uw visie naar een krachtige online aanwezigheid. 
              Gebruik onze configurator voor een strategisch voorstel op maat.
            </p>
          </div>

          <Card className="border-white/5 bg-card/40 backdrop-blur-2xl shadow-2xl shadow-primary/10 overflow-hidden rounded-2xl">
            <CardHeader className="border-b border-white/5 bg-white/5 px-8 py-10">
              <div className="flex items-center gap-5">
                <div className="p-3 bg-primary/10 rounded-xl border border-primary/20 shadow-inner">
                  <Wallet className="size-7 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold tracking-tight">Start Uw Project</CardTitle>
                  <CardDescription className="text-base text-muted-foreground/80">Definieer uw digitale voetafdruk in enkele stappen.</CardDescription>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-8 md:p-12">
              <FormProvider formKey="build_form_contact" formId="build_form_contact" sectionName="laten-we-iets-bouwen" defaultValues={{
              naam: "",
              email: "",
              telefoon: "",
              bedrijfsnaam: "",
              website_type: "",
              budget: BUDGET_OPTIONS[0],
              extra_informatie: ""
            }} validationRules={{
              naam: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              telefoon: {
                required: true,
                pattern: "phone"
              },
              website_type: {
                required: true
              }
            }} submitText="Aanvraag Versturen" successMessage="Ontvangen! Onze strategen duiken in uw aanvraag. U hoort binnen 24 uur van ons." errorMessage="Er is een technische storing opgetreden. Probeer het later opnieuw.">
                <ConfiguratorForm />
              </FormProvider>
            </CardContent>
            
            <CardFooter className="bg-muted/10 border-t border-white/5 px-8 py-6 text-xs text-muted-foreground/60 flex justify-center text-center">
              <p className="max-w-lg leading-relaxed">
                Uw data wordt versleuteld verwerkt conform onze privacyrichtlijnen. 
                Geen spam, enkel pure focus op de realisatie van uw project.
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
      <Toaster />
    </section>;
}